import React from 'react';
import {
  Document,
  Font,
  Page,
  StyleSheet,
  pdf,
} from '@react-pdf/renderer';
import Html from 'react-pdf-html';

export interface IRenderHtmlToPdfOptions {
  html: string;
  size?: 'A4' | any;
  orientation?: 'portrait' | 'landscape';
  margin?: number | string | { top?: number; right?: number; bottom?: number; left?: number };
}

let fontsRegistered = false;
const ensureFontsRegistered = () => {
  if (fontsRegistered) return;
  try {
    Font.register({
      family: 'Helvetica',
      fonts: [
        { src: 'https://cdn.jsdelivr.net/npm/@canvas-fonts/helvetica@1.0.0/Helvetica.woff?raw', fontStyle: 'normal', fontWeight: 'normal' },
      ],
    });
    Font.register({
      family: 'sans-serif',
      fonts: [
        { src: 'https://cdn.jsdelivr.net/npm/@canvas-fonts/helvetica@1.0.0/Helvetica.woff?raw', fontStyle: 'normal', fontWeight: 'normal' },
      ],
    });
  } catch {
    /* noop — Helvetica está disponível como fonte embutida padrão no @react-pdf */
  }
  fontsRegistered = true;
};

const sanitizeHtmlForPdf = (html: string): string => {
  let safe = typeof html === 'string' ? html : '';
  safe = safe.replace(
    /font-family\s*:\s*[^;"']*/gi,
    'font-family: Helvetica',
  );
  safe = safe.replace(/font-family\s*=\s*"[^"]*"/gi, 'font-family="Helvetica"');
  safe = safe.replace(/font-family\s*=\s*'[^']*'/gi, "font-family='Helvetica'");
  safe = safe.replace(/border-collapse\s*:\s*[^;"']*/gi, 'border-collapse: separate');
  safe = safe.replace(/border-collapse\s*=\s*"[^"]*"/gi, 'border-collapse="separate"');
  safe = safe.replace(/border-collapse\s*=\s*'[^']*'/gi, "border-collapse='separate'");
  safe = safe.replace(/border-spacing\s*:\s*[^;"']*/gi, '');
  safe = safe.replace(/border-spacing\s*=\s*"[^"]*"/gi, '');
  safe = safe.replace(/border-spacing\s*=\s*'[^']*'/gi, '');
  return safe;
};

const buildStyles = (margin?: IRenderHtmlToPdfOptions['margin']) => {
  const safeMargin =
    margin ??
    ({
      top: 20,
      right: 20,
      bottom: 20,
      left: 20,
    } as const);

  return StyleSheet.create({
    page: {
      padding: typeof safeMargin === 'object' ? 0 : safeMargin,
      fontFamily: 'Helvetica',
      fontSize: 11,
      color: '#262626',
      ...(typeof safeMargin === 'object'
        ? {
            paddingTop: (safeMargin as any).top ?? 20,
            paddingRight: (safeMargin as any).right ?? 20,
            paddingBottom: (safeMargin as any).bottom ?? 20,
            paddingLeft: (safeMargin as any).left ?? 20,
          }
        : {}),
    },
    htmlWrapper: {
      flex: 1,
    },
  });
};

export const buildCrcPdfDocumentElement = (opts: IRenderHtmlToPdfOptions) => {
  ensureFontsRegistered();
  const { html, size = 'A4', orientation = 'portrait' } = opts;
  const styles = buildStyles(opts.margin);
  const safeHtml =
    typeof html === 'string' && html.trim().length > 0
      ? sanitizeHtmlForPdf(html)
      : '<div><p>Sem conteúdo para exibir.</p></div>';

  return React.createElement(
    Document,
    { title: 'Relatório CRC' },
    React.createElement(
      Page,
      { size, orientation, style: styles.page },
      React.createElement(
        Html as any,
        { style: styles.htmlWrapper },
        safeHtml as any,
      ),
    ),
  );
};

export const renderHtmlToPdfBlob = async (
  opts: IRenderHtmlToPdfOptions,
): Promise<Blob> => {
  const element = buildCrcPdfDocumentElement(opts);
  const blob = await pdf(element as any).toBlob();
  return blob;
};

export const triggerBlobDownload = (blob: Blob, filename: string): void => {
  if (typeof window === 'undefined') return;
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};

export const base64ToString = (base64: string): string => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  const decoder = new TextDecoder('utf-8');
  return decoder.decode(bytes);
};

export default {
  renderHtmlToPdfBlob,
  triggerBlobDownload,
  base64ToString,
  buildCrcPdfDocumentElement,
};
